import React from 'react';
import { Col, Row } from 'react-bootstrap';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import { TextField } from 'Shared/form/TextField';
import SelectField from 'Shared/form/SelectField';
import useFetchEhrBranchSettings from 'App/hooks/useFetchEhrBranchSettings';
import { useFormikContext } from 'formik';
import { states } from 'App/components/PatientForm/constants';
import { PhoneField } from 'App/components/PatientForm/components/PhoneField';
import ToggleSwitchField from 'Shared/form/ToggleSwitchField';
import styled from '@emotion/styled';

const InlineBlockStyle = styled('div')`
    display: inline-block;
`;

const propTypes = {
    timeZoneList: PropTypes.arrayOf(
        PropTypes.shape({
            timeZoneName: PropTypes.string,
            timeZoneId: PropTypes.string,
            timeZoneOffSet: PropTypes.string,
        })
    ).isRequired,
};

function MyAccountForm({ timeZoneList }) {
    const { data: ehrResponse, isLoading } = useFetchEhrBranchSettings();
    const { initialValues, values, submitCount, isSubmitting } = useFormikContext();

    function isDisabled(initialValue, currentValue) {
        return isSubmitting || Boolean(!!initialValue || (!!currentValue && submitCount));
    }

    return (
        <>
            <Row>
                <Col md={12}>
                    <h3 className="section-title">Contact Information</h3>
                </Col>
            </Row>
            <Row>
                <Col md={3}>
                    <TextField
                        fieldName="firstName"
                        label="First Name"
                        placeholder="First Name"
                        required
                        disabled={isDisabled(initialValues.firstName, values.firstName)}
                        maxLength={255}
                    />
                </Col>
                <Col md={3}>
                    <TextField
                        fieldName="lastName"
                        label="Last Name"
                        placeholder="Last Name"
                        required
                        disabled={isDisabled(initialValues.lastName, values.lastName)}
                        maxLength={255}
                    />
                </Col>
                <Col md={3}>
                    <TextField
                        fieldName="addressLine1"
                        label="Address Line 1"
                        placeholder="Address Line 1"
                        disabled={isSubmitting}
                    />
                </Col>
                <Col md={3}>
                    <TextField
                        fieldName="addressLine2"
                        label="Address Line 2"
                        placeholder="Address Line 2"
                        disabled={isSubmitting}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={3}>
                    <TextField
                        fieldName="email"
                        label="Email"
                        placeholder="Email"
                        required
                        disabled={isDisabled(initialValues.email, values.email)}
                        maxLength={100}
                    />
                </Col>
                <Col md={3}>
                    <TextField fieldName="jobTitle" label="Job Title" placeholder="Job Title" disabled={isSubmitting} />
                </Col>
                <Col md={3}>
                    <TextField
                        fieldName="city"
                        label="City"
                        placeholder="City"
                        maxLength={50}
                        disabled={isSubmitting}
                    />
                </Col>
                <Col md={3}>
                    <SelectField
                        options={states}
                        isClearable
                        label="State / Province"
                        fieldName="state"
                        filterOptionType="startsWith"
                        isDisabled={isSubmitting}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={3}>
                    <PhoneField
                        fieldName="telephone"
                        label="Phone Number"
                        placeholder="(###) ###-####"
                        disabled={isSubmitting}
                    />
                </Col>
                <Col md={{ span: 3, offset: 3 }}>
                    <TextField
                        fieldName="zipcode"
                        label="Zip Code"
                        placeholder="Zip Code"
                        maxLength={50}
                        disabled={isSubmitting}
                    />
                </Col>
                <Col md={3}>
                    <TextField
                        fieldName="country"
                        label="Country"
                        placeholder="Country"
                        maxLength={50}
                        disabled={isSubmitting}
                    />
                </Col>
            </Row>
            {!isLoading && ehrResponse?.ehrType && (
                <div data-testid="ehr-configuration">
                    <Row>
                        <Col md={12}>
                            <h3 className="section-title">EHR Configuration</h3>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={3}>
                            <TextField
                                id="ehr_provider_id"
                                data-testid="ehr-provider-id"
                                fieldName="ehrProviderId"
                                label="EHR Provider ID"
                                placeholder="EHR Provider ID"
                                disabled={isSubmitting}
                            />
                        </Col>
                    </Row>
                </div>
            )}
            <Row>
                <Col md={12}>
                    <h3 className="section-title">Preferences</h3>
                </Col>
            </Row>
            <Row>
                <Col md={3}>
                    <SelectField
                        options={timeZoneList}
                        isClearable
                        label="Timezone"
                        fieldName="timezone"
                        getOptionValue={R.prop('timeZoneId')}
                        getOptionLabel={(option) => `${option.timeZoneName} [${option.timeZoneOffSet}]`}
                        isDisabled={isSubmitting}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <InlineBlockStyle>
                        <ToggleSwitchField
                            id="daylight-savings"
                            fieldName="daylightSavings"
                            label="Use Daylight Saving Time"
                            disabled={isSubmitting}
                        />
                    </InlineBlockStyle>
                </Col>
                <Col md={12}>
                    <InlineBlockStyle>
                        <ToggleSwitchField
                            id="message-email"
                            fieldName="messageEmail"
                            label="Enable Email Alerts for TherigySTM Messages"
                            disabled={isSubmitting}
                        />
                    </InlineBlockStyle>
                </Col>
                <Col md={12}>
                    <InlineBlockStyle>
                        <ToggleSwitchField
                            id="check-verbose"
                            fieldName="checkVerbose"
                            label="Fully Scripted Assessments"
                            disabled={isSubmitting}
                        />
                    </InlineBlockStyle>
                </Col>
            </Row>
        </>
    );
}

MyAccountForm.propTypes = propTypes;
export default MyAccountForm;
